/**
 * ACTIVITY-CORE (L1) — deshacer inmediato de sesión.
 *
 * Pila en memoria (se vacía al recargar o cambiar de cuenta) + toast con
 * acción "Deshacer" + Ctrl/Cmd+Z. Cada entrada aplica la mutación INVERSA por
 * el write-path normal de PowerSync (nunca rebobina estado); el deshacer
 * duradero/colaborativo es el revert del feed de actividad (server-side).
 *
 * Criterio de dominio Stockpilot: el undo L1 aplica al CATÁLOGO y a los
 * borradores de operaciones. Los moves confirmados NO registran undo — su
 * corrección es un contramovimiento explícito con motivo (feed de actividad).
 */
import { msg } from "@lingui/core/macro";
import { createUndoStack } from "@stockpilot/shared";
import { showToast } from "@/components/ui/toast";
import { useI18n } from "@/providers/i18n-provider";

/** Pila única de la app (ámbito: pestaña + cuenta activa). */
const appUndoStack = createUndoStack();

interface UseUndoResult {
  /**
   * Registra una acción deshacible y muestra el toast con "Deshacer".
   * `message` llega ya localizado (el caller conoce el contexto).
   */
  registerUndo: (message: string, undo: () => Promise<void> | void) => void;
  /** Deshace la última acción (Ctrl+Z). true si había algo que deshacer. */
  undoLast: () => Promise<boolean>;
  /** Vacía la pila (cambio de cuenta/sesión). */
  clearUndoStack: () => void;
}

export function useUndo(): UseUndoResult {
  const { t } = useI18n();

  const registerUndo = (message: string, undo: () => Promise<void> | void) => {
    const remove = appUndoStack.push({ label: message, undo });
    showToast(message, "info", {
      action: {
        label: t(msg`Undo`),
        onClick: () => {
          // Consumir ESTA acción (aunque ya no sea la última) sin duplicar Ctrl+Z.
          if (remove()) {
            void Promise.resolve(undo()).catch((err: unknown) => {
              console.error("[undo] toast undo failed:", err);
              showToast(t(msg`Could not undo the action`), "error");
            });
          }
        },
      },
    });
  };

  const undoLast = async () => {
    try {
      const undone = await appUndoStack.undoLast();
      if (!undone) {
        return false;
      }
      showToast(t(msg`Action undone`), "success");
      return true;
    } catch (err: unknown) {
      console.error("[undo] Ctrl+Z undo failed:", err);
      showToast(t(msg`Could not undo the action`), "error");
      return true;
    }
  };

  return { registerUndo, undoLast, clearUndoStack: () => appUndoStack.clear() };
}
